import { Impact, isTree } from "@ecologi/shared";
import styled from "@emotion/styled";
import Image from "next/legacy/image";
import React from "react";
import { BIRTHDAY_BLOG_ARTICLES, DIVIDEND_BLOG_URL } from "./consts";
import { getPictogramUrl } from "./utils";

const BoldImpactAmount = styled.span`
  font-weight: bold;
`;

const InfoCard = styled.div`
  display: flex;
  gap: 0 15px;
  border-radius: 16px;
  padding: 15px 20px;
  margin-top: 24px;
  line-height: 1.5;
  background-color: ${({ theme }) => theme.colors.mushroomLight};
  min-height: 80px;

  @media only screen and (max-width: 400px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding-bottom: 24px;
  }
`;

const ModalLink = styled.a`
  border-bottom: 1px solid ${({ theme }) => theme.colors.night};
  color: ${({ theme }) => theme.colors.night};
  hyphens: auto;
  text-decoration: none;

  &:hover {
    border-bottom-color: ${({ theme }) => theme.colors.jungle};
    color: ${({ theme }) => theme.colors.jungle};
  }
`;

const Pictogram = styled.div<{ xl?: boolean }>`
  position: relative;
  width: ${(props) => (props.xl ? "45px" : "38px")};
  flex-shrink: 0;
  max-height: 50px;

  @media only screen and (max-width: 400px) {
    height: 45px;
    margin-bottom: 12px;
  }
`;

const ProjectImpactTextWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
`;

const SpecialTreeText = styled.p`
  font-weight: bold;
`;

const renderProjectImpactText = (
  description: string,
  impactAmount?: string
): JSX.Element | undefined => {
  const PATTERN_TO_REPLACE = "{{x}}";

  if (description.includes(PATTERN_TO_REPLACE)) {
    if (impactAmount) {
      const [description1, description2] =
        description.split(PATTERN_TO_REPLACE);

      return (
        <p>
          {description1}
          <BoldImpactAmount>
            {parseInt(impactAmount).toLocaleString()}
          </BoldImpactAmount>
          {description2}
        </p>
      );
    }

    return;
  }

  return <>{description}</>;
};

export const ModalInfoCard = ({
  impact,
  isOwnProfile,
}: {
  impact: Impact;
  isOwnProfile: boolean;
}) => {
  const pictogramUrl = getPictogramUrl(impact);

  if (!isTree(impact)) {
    const description = impact.project?.impactDescription;
    const renderedProjectImpactText =
      description &&
      renderProjectImpactText(description, impact.project?.impactAmount);

    return (
      <>
        {renderedProjectImpactText && (
          <InfoCard>
            <Pictogram>
              {pictogramUrl && <Image layout="fill" src={pictogramUrl} />}
            </Pictogram>
            <ProjectImpactTextWrapper data-testid="forest-modal-blurb">
              {renderedProjectImpactText}
            </ProjectImpactTextWrapper>
          </InfoCard>
        )}
      </>
    );
  }

  if (impact.localProject || impact.isBirthday || impact.isDividend) {
    return (
      <InfoCard>
        <Pictogram xl={!!impact.localProject}>
          {pictogramUrl && <Image layout="fill" src={pictogramUrl} />}
        </Pictogram>

        <ProjectImpactTextWrapper data-testid="forest-modal-blurb">
          {impact.localProject &&
            `Supporting local reforestation in ${impact.localProject.countryNiceCopy}`}

          {(impact.isBirthday || impact.isDividend) && (
            <>
              <SpecialTreeText>
                {impact.isBirthday &&
                  BIRTHDAY_BLOG_ARTICLES[impact.treeYear].title}
                {impact.isDividend
                  ? isOwnProfile
                    ? "Your tree dividend"
                    : "Tree dividend"
                  : null}
              </SpecialTreeText>

              <p>
                {`More info in our `}
                <ModalLink
                  href={
                    impact.isBirthday
                      ? BIRTHDAY_BLOG_ARTICLES[impact.treeYear].blogUrl
                      : DIVIDEND_BLOG_URL
                  }
                  target="_blank"
                  title="Read our blog post (new tab)"
                  rel="noopener"
                >
                  blog post
                </ModalLink>
              </p>
            </>
          )}
        </ProjectImpactTextWrapper>
      </InfoCard>
    );
  }

  return (
    <InfoCard>
      <p>
        {`Your contribution helps fund all the projects within our global reforestation mix. This tile represents trees in one of these projects. `}
        <ModalLink
          href={"/articles/blog/where-are-my-trees"}
          rel="noopener"
          target="_blank"
        >
          Learn more
        </ModalLink>
      </p>
    </InfoCard>
  );
};
